import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import {
  Activity,
  Loader2,
  Search,
  Undo2,
  Filter,
  Download,
  RefreshCw,
  Shield,
  UserPlus,
  UserMinus,
  Edit,
  Eye,
  Upload,
  Send,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Calendar,
  User,
  Clock,
  FileText,
  FileSpreadsheet,
  FileType2,
  FileDown,
  BarChart3,
  TrendingUp,
  Users,
  Package,
  Settings,
  Trash2,
  Copy,
  LogIn,
  LogOut,
  RotateCcw,
  Power,
  Database,
  Archive,
  X
} from "lucide-react";

// Action configuration with icons and colors
const ACTION_CONFIG = {
  create: { label: "Creación", icon: UserPlus, color: "bg-green-100 text-green-700 border-green-200" },
  update: { label: "Actualización", icon: Edit, color: "bg-blue-100 text-blue-700 border-blue-200" },
  delete: { label: "Eliminación", icon: Trash2, color: "bg-red-100 text-red-700 border-red-200" },
  bulk_delete: { label: "Eliminación Masiva", icon: Trash2, color: "bg-red-100 text-red-700 border-red-200" },
  view: { label: "Visualización", icon: Eye, color: "bg-slate-100 text-slate-700 border-slate-200" },
  export: { label: "Exportación", icon: Download, color: "bg-purple-100 text-purple-700 border-purple-200" },
  import: { label: "Importación", icon: Upload, color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  approve: { label: "Aprobación", icon: CheckCircle, color: "bg-green-100 text-green-700 border-green-200" },
  reject: { label: "Rechazo", icon: XCircle, color: "bg-orange-100 text-orange-700 border-orange-200" },
  assign: { label: "Asignación", icon: Users, color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  unassign: { label: "Desasignación", icon: Users, color: "bg-amber-100 text-amber-700 border-amber-200" },
  login: { label: "Inicio de Sesión", icon: LogIn, color: "bg-teal-100 text-teal-700 border-teal-200" },
  logout: { label: "Cierre de Sesión", icon: LogOut, color: "bg-slate-100 text-slate-700 border-slate-200" },
  upload: { label: "Carga de Archivo", icon: Upload, color: "bg-violet-100 text-violet-700 border-violet-200" },
  download: { label: "Descarga", icon: Download, color: "bg-sky-100 text-sky-700 border-sky-200" },
  send: { label: "Envío", icon: Send, color: "bg-blue-100 text-blue-700 border-blue-200" },
  cancel: { label: "Cancelación", icon: X, color: "bg-rose-100 text-rose-700 border-rose-200" },
  restore: { label: "Restauración", icon: RotateCcw, color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  configure: { label: "Configuración", icon: Settings, color: "bg-gray-100 text-gray-700 border-gray-200" },
  activate: { label: "Activación", icon: Power, color: "bg-green-100 text-green-700 border-green-200" },
  deactivate: { label: "Desactivación", icon: Power, color: "bg-red-100 text-red-700 border-red-200" },
  clone: { label: "Clonación", icon: Copy, color: "bg-purple-100 text-purple-700 border-purple-200" },
  reset: { label: "Reinicio", icon: RotateCcw, color: "bg-amber-100 text-amber-700 border-amber-200" },
  bulk_update: { label: "Actualización Masiva", icon: Edit, color: "bg-blue-100 text-blue-700 border-blue-200" }
};

// Module icons mapping
const MODULE_ICONS = {
  usuarios: Users,
  roles: Shield,
  tickets: FileText,
  mensajeria: Send,
  inventario: Package,
  mobiliario: Package,
  requisiciones: FileText,
  visitantes: Users,
  gestion_humana: Users,
  vacaciones: Calendar,
  permisos: Shield,
  nomina: FileSpreadsheet,
  reclutamiento: Users,
  whatsapp: Send,
  caja_chica: Database,
  cuentas_pagar: FileSpreadsheet,
  medicamentos: Package,
  riesgos_generales: Shield,
  riesgos_personas: Users,
  documentos: FileText,
  comparaciones: BarChart3,
  plantillas: FileType2,
  configuracion: Settings,
  reportes: BarChart3,
  alertas: Activity,
  backup: Database,
  auth: LogIn,
  sistema: Settings
};

export default function OperacionesPorUsuarioPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [moduloFilter, setModuloFilter] = useState("all");
  const [accionFilter, setAccionFilter] = useState("all");
  const [usuarioFilter, setUsuarioFilter] = useState("all");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [page, setPage] = useState(0);
  const [totalLogs, setTotalLogs] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [revertedFields, setRevertedFields] = useState(new Set());
  const [revertingField, setRevertingField] = useState(null);
  const [restoredLogs, setRestoredLogs] = useState(new Set());
  const [restoring, setRestoring] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [modulos, setModulos] = useState([]);
  const [moduloSearch, setModuloSearch] = useState("");
  const [acciones, setAcciones] = useState([]);
  const [usuariosActivos, setUsuariosActivos] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [purgeDays, setPurgeDays] = useState(90);
  const [purging, setPurging] = useState(false);
  const [purgeRuns, setPurgeRuns] = useState([]);
  const [incluirArchivo, setIncluirArchivo] = useState(false);
  const [archiveTotal, setArchiveTotal] = useState(0);
  const [userActivityOpen, setUserActivityOpen] = useState(false);
  const [userActivityTarget, setUserActivityTarget] = useState(null);
  const [userActivityLogs, setUserActivityLogs] = useState([]);
  const [userActivityLoading, setUserActivityLoading] = useState(false);
  const pageSize = 30;
  const { getAuthHeaders, user } = useAuth();

  // Fetch available modules and actions
  const fetchFilters = useCallback(async () => {
    try {
      const [modulosRes, usuariosRes] = await Promise.all([
        axios.get(`${API}/operations-audit/modulos`, getAuthHeaders()),
        axios.get(`${API}/operations-audit/usuarios-activos`, getAuthHeaders())
      ]);
      setModulos(modulosRes.data.modulos || []);
      setAcciones(modulosRes.data.acciones || []);
      setUsuariosActivos(usuariosRes.data.usuarios || []);
    } catch (err) {
      console.error("Error fetching filters:", err);
    }
  }, [getAuthHeaders]);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("limit", pageSize);
      params.append("skip", page * pageSize);
      if (moduloFilter !== "all") params.append("modulo", moduloFilter);
      if (accionFilter !== "all") params.append("accion", accionFilter);
      if (usuarioFilter !== "all") params.append("usuario_id", usuarioFilter);
      if (fechaInicio) params.append("fecha_inicio", fechaInicio);
      if (fechaFin) params.append("fecha_fin", fechaFin);
      if (searchTerm) params.append("busqueda", searchTerm);
      if (incluirArchivo) params.append("incluir_archivo", "true");

      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/operations-audit?${params.toString()}`, getAuthHeaders()),
        axios.get(`${API}/operations-audit/stats`, getAuthHeaders())
      ]);

      setLogs(logsRes.data.logs || []);
      setTotalLogs(logsRes.data.total || 0);
      setArchiveTotal(logsRes.data.archive_total || 0);
      setStats(statsRes.data || null);
    } catch (err) {
      console.error("Error fetching logs:", err);
      if (err.response?.status === 403) {
        toast.error("No tienes permisos para ver el log de operaciones");
      } else {
        toast.error("Error al cargar el log de operaciones");
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, page, moduloFilter, accionFilter, usuarioFilter, fechaInicio, fechaFin, searchTerm, incluirArchivo]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString("es", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Hace un momento";
    if (minutes < 60) return `Hace ${minutes} min`;
    if (hours < 24) return `Hace ${hours}h`;
    if (days < 7) return `Hace ${days}d`;
    return formatDate(timestamp);
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const params = new URLSearchParams();
      if (moduloFilter !== "all") params.append("modulo", moduloFilter);
      if (accionFilter !== "all") params.append("accion", accionFilter);
      if (usuarioFilter !== "all") params.append("usuario_id", usuarioFilter);
      if (fechaInicio) params.append("fecha_inicio", fechaInicio);
      if (fechaFin) params.append("fecha_fin", fechaFin);
      if (searchTerm) params.append("busqueda", searchTerm);
      if (incluirArchivo) params.append("incluir_archivo", "true");

      const response = await axios.get(
        `${API}/operations-audit/export/${format}?${params.toString()}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );

      // Create download link
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `operaciones_auditoria.${format === 'excel' ? 'xlsx' : format === 'word' ? 'docx' : 'html'}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exportación a ${format.toUpperCase()} completada`);
    } catch (err) {
      console.error("Error exporting:", err);
      toast.error("Error al exportar");
    } finally {
      setExporting(false);
    }
  };

  const openUserActivity = async (log) => {
    const uid = log?.usuario_id;
    if (!uid) {
      toast.error("Este registro no tiene usuario identificable");
      return;
    }
    setUserActivityTarget({
      id: uid,
      name: log?.usuario_nombre || log?.usuario_email || "Usuario",
      email: log?.usuario_email || "",
      rol: log?.usuario_rol || "",
    });
    setUserActivityOpen(true);
    setUserActivityLoading(true);
    setUserActivityLogs([]);
    try {
      const params = new URLSearchParams();
      params.append("limit", "100");
      params.append("usuario_id", uid);
      params.append("incluir_archivo", "true");
      const res = await axios.get(`${API}/operations-audit?${params.toString()}`, getAuthHeaders());
      setUserActivityLogs(res.data?.logs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar actividad del usuario");
    } finally {
      setUserActivityLoading(false);
    }
  };

  const applyUserFilter = () => {
    if (!userActivityTarget?.id) return;
    setUsuarioFilter(userActivityTarget.id);
    setUserActivityOpen(false);
    setPage(0);
  };

  const clearFilters = () => {
    setModuloFilter("all");
    setAccionFilter("all");
    setUsuarioFilter("all");
    setFechaInicio("");
    setFechaFin("");
    setSearchTerm("");
    setPage(0);
  };

  const openPurgeDialog = async () => {
    setShowPurgeDialog(true);
    try {
      const res = await axios.get(`${API}/operations-audit/purgar/historial`, getAuthHeaders());
      setPurgeRuns(res.data?.runs || []);
    } catch (err) {
      // non-blocking — dialog still opens
    }
  };

  const handlePurge = async () => {
    const dias = parseInt(purgeDays, 10);
    if (isNaN(dias) || dias < 7) {
      toast.error("Ingrese al menos 7 días");
      return;
    }
    if (!window.confirm(`¿Archivar todos los registros con más de ${dias} días? Se moverán a la colección de archivo (no se eliminan permanentemente).`)) {
      return;
    }
    try {
      setPurging(true);
      const res = await axios.post(
        `${API}/operations-audit/purgar?dias=${dias}`,
        {},
        getAuthHeaders()
      );
      toast.success(res.data?.message || `${res.data?.archivados || 0} registros archivados`);
      setShowPurgeDialog(false);
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al archivar registros");
    } finally {
      setPurging(false);
    }
  };

  const handleRevertirCampo = async (campo, antes) => {
    if (!window.confirm(`¿Revertir el campo "${campo}" a su valor anterior (${antes === null || antes === "" ? "vacío" : antes})?`)) return;
    try {
      setRevertingField(campo);
      const res = await axios.post(`${API}/operations-audit/${selectedLog.id}/revertir`, { campo }, getAuthHeaders());
      toast.success(res.data?.message || "Cambio revertido");
      setRevertedFields(prev => new Set([...prev, campo]));
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo revertir el cambio");
    } finally {
      setRevertingField(null);
    }
  };

  const handleRestaurarEliminado = async () => {
    if (!window.confirm("¿Restaurar el registro eliminado a partir de la copia guardada?")) return;
    try {
      setRestoring(true);
      const res = await axios.post(`${API}/operations-audit/${selectedLog.id}/restaurar`, {}, getAuthHeaders());
      toast.success(res.data?.message || "Registro restaurado");
      setRestoredLogs(prev => new Set([...prev, selectedLog.id]));
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo restaurar el registro");
    } finally {
      setRestoring(false);
    }
  };

  const totalPages = Math.ceil(totalLogs / pageSize);
  const isAdmin = user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "administrador";

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
          <div className="flex flex-col items-center justify-center h-full">
            <Shield className="w-16 h-16 text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-600">Acceso Restringido</h2>
            <p className="text-slate-500">Solo administradores pueden ver las operaciones por usuario</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="operations-audit-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                Operaciones por Usuario
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Registro completo de todas las transacciones del sistema
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchLogs} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <label
              className={`inline-flex items-center gap-2 px-3 h-9 rounded-md border text-sm cursor-pointer transition-colors ${
                incluirArchivo
                  ? "bg-[#193966] text-white border-[#193966]"
                  : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 hover:bg-slate-50"
              }`}
              data-testid="toggle-incluir-archivo"
              title="Incluir registros archivados (mayores a 90 días)"
            >
              <input
                type="checkbox"
                className="hidden"
                checked={incluirArchivo}
                onChange={(e) => { setIncluirArchivo(e.target.checked); setPage(0); }}
              />
              <Archive className="w-4 h-4" />
              Incluir archivados
              {incluirArchivo && archiveTotal > 0 && (
                <Badge className="bg-white/20 text-white border-0 ml-1">{archiveTotal}</Badge>
              )}
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={openPurgeDialog}
              className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:border-amber-700"
              data-testid="open-purge-dialog-btn"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archivar
            </Button>
            <div className="relative">
              <Select value="" onValueChange={handleExport} disabled={exporting}>
                <SelectTrigger className="w-[140px]">
                  <Download className="w-4 h-4 mr-2" />
                  {exporting ? "Exportando..." : "Exportar"}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excel">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4" />
                      Excel (.xlsx)
                    </div>
                  </SelectItem>
                  <SelectItem value="pdf">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      PDF (.html)
                    </div>
                  </SelectItem>
                  <SelectItem value="word">
                    <div className="flex items-center gap-2">
                      <FileType2 className="w-4 h-4" />
                      Word (.docx)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <Card className="border-slate-200">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 font-medium">Total Operaciones</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.total?.toLocaleString() || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Database className="w-5 h-5 text-slate-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-emerald-600 font-medium">Hoy</p>
                    <p className="text-2xl font-bold text-emerald-700">{stats.today_count?.toLocaleString() || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-blue-600 font-medium">Creaciones</p>
                    <p className="text-2xl font-bold text-blue-700">{stats.by_action?.create?.toLocaleString() || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-amber-600 font-medium">Actualizaciones</p>
                    <p className="text-2xl font-bold text-amber-700">{stats.by_action?.update?.toLocaleString() || 0}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Edit className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-rose-200 bg-rose-50/50">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-rose-600 font-medium">Eliminaciones</p>
                    <p className="text-2xl font-bold text-rose-700">{(stats.by_action?.delete || 0) + (stats.by_action?.bulk_delete || 0)}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-rose-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-5">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
              {/* Search */}
              <div className="md:col-span-2">
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Buscar</label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar descripción, usuario..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                    className="pl-9 h-9"
                    data-testid="search-input"
                  />
                </div>
              </div>

              {/* Module filter */}
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Módulo</label>
                <Select value={moduloFilter} onValueChange={(v) => { setModuloFilter(v); setPage(0); }}>
                  <SelectTrigger className="h-9" data-testid="modulo-filter">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[60vh]">
                    <div className="p-2 sticky top-0 bg-white z-10 border-b">
                      <Input
                        placeholder="Buscar módulo..."
                        value={moduloSearch}
                        onChange={(e) => setModuloSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        className="h-8 text-xs"
                        data-testid="modulo-search-input"
                      />
                    </div>
                    <SelectItem value="all">Todos los módulos</SelectItem>
                    {modulos
                      .filter(m => !moduloSearch || (m.label || "").toLowerCase().includes(moduloSearch.toLowerCase()) || (m.id || "").toLowerCase().includes(moduloSearch.toLowerCase()))
                      .map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                      ))}
                    {modulos.length === 0 && (
                      <div className="px-2 py-3 text-xs text-slate-400 text-center">Cargando módulos...</div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Action filter */}
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Acción</label>
                <Select value={accionFilter} onValueChange={(v) => { setAccionFilter(v); setPage(0); }}>
                  <SelectTrigger className="h-9" data-testid="accion-filter">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las acciones</SelectItem>
                    {acciones.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* User filter */}
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Usuario</label>
                <Select value={usuarioFilter} onValueChange={(v) => { setUsuarioFilter(v); setPage(0); }}>
                  <SelectTrigger className="h-9" data-testid="usuario-filter">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los usuarios</SelectItem>
                    {usuariosActivos.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.nombre || u.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Clear filters */}
              <div>
                <Button variant="outline" size="sm" onClick={clearFilters} className="w-full h-9">
                  <X className="w-4 h-4 mr-1" />
                  Limpiar
                </Button>
              </div>
            </div>

            {/* Date filters row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Fecha Desde</label>
                <Input
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => { setFechaInicio(e.target.value); setPage(0); }}
                  className="h-9"
                  data-testid="fecha-inicio"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Fecha Hasta</label>
                <Input
                  type="date"
                  value={fechaFin}
                  onChange={(e) => { setFechaFin(e.target.value); setPage(0); }}
                  className="h-9"
                  data-testid="fecha-fin"
                />
              </div>
              <div className="md:col-span-2 flex items-end">
                <p className="text-xs text-slate-500">
                  Mostrando {logs.length} de {totalLogs.toLocaleString()} registros
                  {incluirArchivo && archiveTotal > 0 && (
                    <span className="ml-1 text-amber-700 dark:text-amber-300">
                      (+ {archiveTotal.toLocaleString()} archivados)
                    </span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Operations Timeline */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : logs.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="py-16 text-center">
              <Activity className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-600 mb-2">Sin operaciones registradas</h3>
              <p className="text-slate-500">
                {searchTerm || moduloFilter !== "all" || accionFilter !== "all" || usuarioFilter !== "all" || fechaInicio || fechaFin
                  ? "No se encontraron operaciones con los filtros aplicados"
                  : "Las operaciones del sistema aparecerán aquí"
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {logs.map((log, index) => {
              const actionConfig = ACTION_CONFIG[log.accion] || { label: log.accion_label || log.accion, icon: Activity, color: "bg-slate-100 text-slate-700 border-slate-200" };
              const ActionIcon = actionConfig.icon;
              const ModuleIcon = MODULE_ICONS[log.modulo] || Settings;
              
              return (
                <Card 
                  key={log.id || index} 
                  className={`hover:shadow-md transition-all cursor-pointer border-l-4 ${
                    log.accion === 'delete' || log.accion === 'bulk_delete' ? 'border-l-red-400' :
                    log.accion === 'create' ? 'border-l-green-400' :
                    log.accion === 'update' ? 'border-l-blue-400' :
                    log.accion === 'approve' ? 'border-l-emerald-400' :
                    log.accion === 'reject' ? 'border-l-orange-400' :
                    'border-l-slate-300'
                  }`}
                  onClick={() => { setSelectedLog(log); setRevertedFields(new Set()); setShowDetails(true); }}
                  data-testid={`operation-log-${log.id}`}
                >
                  <CardContent className="py-3 px-4">
                    <div className="flex items-start gap-3">
                      {/* Action Icon */}
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${actionConfig.color}`}>
                        <ActionIcon className="w-4 h-4" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge variant="outline" className={`text-xs ${actionConfig.color}`}>
                            {actionConfig.label}
                          </Badge>
                          <Badge variant="secondary" className="text-xs flex items-center gap-1">
                            <ModuleIcon className="w-3 h-3" />
                            {log.modulo_label || log.modulo}
                          </Badge>
                          {log.entidad && (
                            <span className="text-xs text-slate-500">{log.entidad}</span>
                          )}
                          {log._from_archive && (
                            <Badge
                              variant="outline"
                              className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300 flex items-center gap-1"
                              data-testid={`archived-badge-${log.id}`}
                              title="Registro proveniente del archivo histórico"
                            >
                              <Archive className="w-3 h-3" />
                              Archivado
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-1">
                          {log.descripcion}
                        </p>

                        <div className="flex items-center gap-4 mt-1.5 text-xs text-slate-500">
                          <button
                            type="button"
                            className="flex items-center gap-1 hover:text-[#193966] hover:underline focus:outline-none focus:text-[#193966] transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              openUserActivity(log);
                            }}
                            data-testid={`user-clickthrough-${log.id}`}
                            title={`Ver actividad de ${log.usuario_nombre || log.usuario_email}`}
                          >
                            <User className="w-3 h-3" />
                            {log.usuario_nombre || log.usuario_email}
                          </button>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatRelativeTime(log.timestamp)}
                          </span>
                          {(log.entidad_nombre || log.entidad_id) && (
                            <span className="text-slate-400 truncate max-w-[220px]">
                              {log.entidad_nombre || `ID: ${log.entidad_id.slice(0, 8)}...`}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Time badge */}
                      <div className="text-right text-xs text-slate-400 flex-shrink-0">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {formatDate(log.timestamp).split(",")[0]}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-sm text-slate-500">
                  Página {page + 1} de {totalPages} ({totalLogs.toLocaleString()} registros)
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Details Dialog */}
        <Dialog open={showDetails} onOpenChange={setShowDetails}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Detalles de la Operación
              </DialogTitle>
              <DialogDescription>
                Información completa del registro de auditoría
              </DialogDescription>
            </DialogHeader>
            
            {selectedLog && (
              <div className="space-y-4 mt-4">
                {/* Main info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-slate-500">Módulo</label>
                    <p className="font-medium text-slate-900">{selectedLog.modulo_label || selectedLog.modulo}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Acción</label>
                    <Badge className={ACTION_CONFIG[selectedLog.accion]?.color || "bg-slate-100"}>
                      {selectedLog.accion_label || selectedLog.accion}
                    </Badge>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Entidad</label>
                    <p className="font-medium text-slate-900">{selectedLog.entidad || "-"}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Entidad Afectada</label>
                    <p className="font-medium text-slate-900" data-testid="audit-entidad-nombre">
                      {selectedLog.entidad_nombre || selectedLog.entidad_id || "-"}
                    </p>
                    {selectedLog.entidad_nombre && selectedLog.entidad_id && (
                      <p className="font-mono text-[10px] text-slate-400 mt-0.5">{selectedLog.entidad_id}</p>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-xs font-medium text-slate-500">Descripción</label>
                  <p className="text-slate-900 bg-slate-50 p-3 rounded-lg mt-1">{selectedLog.descripcion}</p>
                </div>

                {/* User info */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <label className="text-xs font-medium text-slate-500">Usuario</label>
                    <p className="font-medium text-slate-900">{selectedLog.usuario_nombre}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Email</label>
                    <p className="text-sm text-slate-700">{selectedLog.usuario_email}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-500">Rol</label>
                    <p className="text-sm text-slate-700">{selectedLog.usuario_rol || "-"}</p>
                  </div>
                </div>

                {/* Additional details */}
                {selectedLog.detalles && Object.keys(selectedLog.detalles).length > 0 && (
                  <div className="space-y-3">
                    <label className="text-xs font-medium text-slate-500">Detalles Adicionales</label>

                    {/* Movimiento realizado: resumen legible de qué se hizo */}
                    {selectedLog.detalles.cambios && Object.keys(selectedLog.detalles.cambios).length > 0 && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 space-y-1" data-testid="audit-movimiento-resumen">
                        <p className="text-xs font-semibold text-blue-800 dark:text-blue-300">Movimiento realizado:</p>
                        {Object.entries(selectedLog.detalles.cambios).map(([campo, c]) => {
                          const label = campo.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
                          const antes = c?.antes === null || c?.antes === undefined || c?.antes === "" ? "(vacío)" : String(c.antes);
                          const despues = c?.despues === null || c?.despues === undefined || c?.despues === "" ? "(vacío)" : String(c.despues);
                          return (
                            <p key={campo} className="text-xs text-blue-900 dark:text-blue-200">
                              {c?.antes === "(estructura)"
                                ? <>• Se modificó la estructura de <strong>{label}</strong></>
                                : antes === "(vacío)"
                                  ? <>• Se estableció <strong>{label}</strong> en «{despues}»</>
                                  : despues === "(vacío)"
                                    ? <>• Se quitó el valor de <strong>{label}</strong> (era «{antes}»)</>
                                    : <>• Se cambió <strong>{label}</strong> de «{antes}» a «{despues}»</>}
                            </p>
                          );
                        })}
                      </div>
                    )}

                    {/* Qué se modificó: campo, antes y después */}
                    {selectedLog.detalles.cambios && Object.keys(selectedLog.detalles.cambios).length > 0 && (
                      <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="audit-cambios">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium text-slate-600">Campo modificado</th>
                              <th className="text-left px-3 py-2 font-medium text-slate-600">Antes</th>
                              <th className="text-left px-3 py-2 font-medium text-slate-600">Después</th>
                              <th className="text-right px-3 py-2 font-medium text-slate-600">Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(selectedLog.detalles.cambios).map(([campo, c]) => (
                              <tr key={campo} className="border-t border-slate-100">
                                <td className="px-3 py-2 font-medium text-slate-800">{campo}</td>
                                <td className="px-3 py-2 text-red-600">{c?.antes === null || c?.antes === undefined || c?.antes === "" ? "—" : String(c.antes)}</td>
                                <td className="px-3 py-2 text-emerald-700">{c?.despues === null || c?.despues === undefined || c?.despues === "" ? "—" : String(c.despues)}</td>
                                <td className="px-3 py-2 text-right">
                                  {c?.antes === "(estructura)" ? (
                                    <span className="text-slate-300">—</span>
                                  ) : revertedFields.has(campo) ? (
                                    <Badge className="bg-emerald-100 text-emerald-700">Revertido</Badge>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 px-2 text-[11px] gap-1"
                                      disabled={revertingField === campo}
                                      onClick={() => handleRevertirCampo(campo, c?.antes)}
                                      data-testid={`revertir-campo-${campo}`}
                                    >
                                      {revertingField === campo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}
                                      Revertir
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Datos enviados (cuando no hay diff disponible) */}
                    {!selectedLog.detalles.cambios && selectedLog.detalles.datos_enviados && Object.keys(selectedLog.detalles.datos_enviados).length > 0 && (
                      <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="audit-datos-enviados">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium text-slate-600">Campo</th>
                              <th className="text-left px-3 py-2 font-medium text-slate-600">Valor enviado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(selectedLog.detalles.datos_enviados).map(([campo, v]) => (
                              <tr key={campo} className="border-t border-slate-100">
                                <td className="px-3 py-2 font-medium text-slate-800">{campo}</td>
                                <td className="px-3 py-2 text-slate-700">{v === null || v === undefined || v === "" ? "—" : (typeof v === "object" ? JSON.stringify(v) : String(v))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Snapshot de documento eliminado → restauración */}
                    {selectedLog.detalles.snapshot && (
                      <div className="flex items-center justify-between gap-3 border border-amber-200 bg-amber-50 rounded-lg px-3 py-2">
                        <p className="text-xs text-amber-800">
                          Este registro guarda una copia completa del documento eliminado.
                        </p>
                        {restoredLogs.has(selectedLog.id) ? (
                          <Badge className="bg-emerald-100 text-emerald-700 shrink-0">Restaurado</Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs gap-1 border-amber-300 text-amber-800 hover:bg-amber-100 shrink-0"
                            disabled={restoring}
                            onClick={handleRestaurarEliminado}
                            data-testid="restaurar-eliminado-btn"
                          >
                            {restoring ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}
                            Restaurar registro
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Registros antiguos sin detalle de campos */}
                    {!selectedLog.detalles.cambios && !selectedLog.detalles.datos_enviados && !selectedLog.detalles.snapshot && selectedLog.accion === "update" && (
                      <p className="text-xs text-slate-400 italic" data-testid="audit-sin-detalle">
                        Este registro no guardó el detalle de los campos modificados (es anterior a la mejora de auditoría).
                        Los registros nuevos muestran exactamente qué se cambió, con el valor anterior y el nuevo.
                      </p>
                    )}

                    {/* Referencia técnica (método, ruta y estado) */}
                    {selectedLog.detalles.path && (
                      <p className="text-[11px] font-mono text-slate-500 bg-slate-50 px-3 py-2 rounded-lg break-all">
                        {selectedLog.detalles.method} {selectedLog.detalles.path} · {selectedLog.detalles.status}
                      </p>
                    )}

                    {/* Otros detalles no estructurados */}
                    {(() => {
                      const otros = Object.fromEntries(Object.entries(selectedLog.detalles).filter(([k]) => !["method", "path", "status", "cambios", "datos_enviados", "snapshot", "snapshot_collection"].includes(k)));
                      return Object.keys(otros).length > 0 ? (
                        <pre className="mt-1 text-xs bg-slate-900 text-slate-100 p-3 rounded-lg overflow-x-auto max-h-40">
                          {JSON.stringify(otros, null, 2)}
                        </pre>
                      ) : null;
                    })()}
                  </div>
                )}

                {/* Timestamp and ID */}
                <div className="flex justify-between text-xs text-slate-500 pt-4 border-t">
                  <span>ID: {selectedLog.id}</span>
                  <span>Fecha: {formatDate(selectedLog.timestamp)}</span>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Purge / Archive Dialog */}
        <Dialog open={showPurgeDialog} onOpenChange={setShowPurgeDialog}>
          <DialogContent className="max-w-lg" data-testid="purge-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Archive className="w-5 h-5 text-amber-600" />
                Archivar registros antiguos
              </DialogTitle>
              <DialogDescription>
                Los registros anteriores a la cantidad de días indicada se moverán a la
                colección <code className="text-xs">operations_audit_archive</code>. No se
                eliminan permanentemente — pueden consultarse cuando sea necesario.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium">Días de antigüedad (mínimo 7)</label>
                <Input
                  type="number"
                  min={7}
                  max={3650}
                  value={purgeDays}
                  onChange={(e) => setPurgeDays(e.target.value)}
                  data-testid="purge-days-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  El sistema además ejecuta archivado automático cada 7 días para logs {'>'} 90 días.
                </p>
              </div>
              {purgeRuns.length > 0 && (
                <div className="bg-slate-50 dark:bg-slate-800 rounded-md p-3 max-h-48 overflow-auto">
                  <p className="text-xs font-medium mb-2 text-slate-600 dark:text-slate-300">
                    Últimas ejecuciones
                  </p>
                  <ul className="text-xs space-y-1">
                    {purgeRuns.slice(0, 6).map((r) => (
                      <li key={r.id} className="flex justify-between gap-2">
                        <span>{new Date(r.ran_at).toLocaleString("es")}</span>
                        <span className="text-slate-500">
                          {r.trigger === "manual" ? "manual" : "auto"} — {r.result?.archived ?? 0} archivados
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowPurgeDialog(false)} disabled={purging}>
                  Cancelar
                </Button>
                <Button
                  onClick={handlePurge}
                  disabled={purging}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="confirm-purge-btn"
                >
                  {purging ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Archivando…</>
                  ) : (
                    <><Archive className="w-4 h-4 mr-2" /> Archivar ahora</>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* User Activity Modal */}
        <Dialog open={userActivityOpen} onOpenChange={setUserActivityOpen}>
          <DialogContent className="max-w-3xl" data-testid="user-activity-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-[#193966]" />
                Actividad de {userActivityTarget?.name || "Usuario"}
              </DialogTitle>
              <DialogDescription className="flex flex-wrap items-center gap-2 pt-1">
                <span>{userActivityTarget?.email}</span>
                {userActivityTarget?.rol && (
                  <Badge variant="outline" className="text-xs">
                    {userActivityTarget.rol}
                  </Badge>
                )}
              </DialogDescription>
            </DialogHeader>

            {userActivityLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[#193966]" />
              </div>
            ) : (
              <>
                {/* Quick stats */}
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[
                    { label: "Total", value: userActivityLogs.length, color: "bg-[#193966]" },
                    { label: "Creaciones", value: userActivityLogs.filter(l => l.accion === "create").length, color: "bg-[#80bc00]" },
                    { label: "Actualizaciones", value: userActivityLogs.filter(l => l.accion === "update").length, color: "bg-blue-500" },
                    { label: "Eliminaciones", value: userActivityLogs.filter(l => l.accion === "delete").length, color: "bg-red-500" },
                  ].map((s) => (
                    <div key={s.label} className="rounded-md border p-2 text-center">
                      <div className={`mx-auto mb-1 h-1.5 w-8 rounded-full ${s.color}`} />
                      <div className="text-lg font-semibold">{s.value}</div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="max-h-[420px] overflow-y-auto border rounded-md divide-y">
                  {userActivityLogs.length === 0 ? (
                    <p className="text-center text-sm text-slate-500 py-8">
                      Sin actividad registrada para este usuario.
                    </p>
                  ) : (
                    userActivityLogs.map((log) => (
                      <div
                        key={log.id}
                        className="p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 text-sm"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-0.5">
                              <Badge variant="outline" className="text-[10px]">
                                {log.modulo_label || log.modulo}
                              </Badge>
                              <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                                {log.accion_label || log.accion}
                              </span>
                              {log._from_archive && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] border-amber-300 text-amber-700 bg-amber-50"
                                >
                                  <Archive className="w-3 h-3 mr-1" /> Archivado
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 dark:text-slate-300 truncate">
                              {log.descripcion}
                            </p>
                          </div>
                          <span className="text-[11px] text-slate-400 flex-shrink-0">
                            {formatRelativeTime(log.timestamp)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="flex justify-between items-center pt-3">
                  <p className="text-xs text-slate-500">
                    Mostrando últimas {userActivityLogs.length} operaciones (incluye archivados)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setUserActivityOpen(false)}
                      data-testid="user-activity-close-btn"
                    >
                      Cerrar
                    </Button>
                    <Button
                      onClick={applyUserFilter}
                      className="bg-[#193966] hover:bg-[#112c4f] text-white"
                      data-testid="user-activity-filter-btn"
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      Filtrar listado por este usuario
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
